// This wrapper component displays a custom WeekView component
//   inside the existing react-big-calendar

import React, { useMemo } from 'react';
import { Navigate } from 'react-big-calendar';
import * as R from 'ramda';

import WeekView from './WeekView';

interface Props {
  date: Date; // Date passed to the view. Week range is is calculated below
  localizer: any;
}

const getDaysOfWeek = (date: Date, localizer: any): Date[] => {
  const firstOfWeek = localizer.startOfWeek();
  const sun = localizer.startOf(date, 'week', firstOfWeek);
  const calculateDateOfWeek = (day: number) => localizer.add(sun, day, 'days');
  return R.times(calculateDateOfWeek, 7);
};

const WeekViewWrapper = ({ date, localizer }: Props) => {
  const datesOfWeek = useMemo(() => getDaysOfWeek(date, localizer), [date]);

  return <WeekView date={date} datesOfWeek={datesOfWeek} />;
};

// Below methods added directly to WeekView are required
//  by react-big-calendar to handle the header date and navigation logic
//  see https://jquense.github.io/react-big-calendar/examples/index.html?path=/docs/guides-creating-custom-views--page
WeekViewWrapper.range = (date, { localizer }) => {
  const datesOfWeek = getDaysOfWeek(date, localizer);
  const start = datesOfWeek[0];
  const end = datesOfWeek[6];
  return localizer.range(start, end);
};

WeekViewWrapper.navigate = (date, action, { localizer }) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return localizer.add(date, -1, 'week');

    case Navigate.NEXT:
      return localizer.add(date, 1, 'week');

    default:
      return date;
  }
};

WeekViewWrapper.title = (date, { localizer }) => {
  const [start, ...rest] = WeekViewWrapper.range(date, { localizer });
  return localizer.format({ start, end: rest.pop() }, 'dayRangeHeaderFormat');
};

export default WeekViewWrapper;
